import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  createInterview,
  deleteInterview,
  getCandidates,
  getInterviewUsers,
  getInterviews,
  updateCandidate,
  updateInterview,
} from "../api/recruitmentApi";
import { formatDate, normalizeList, statusClass } from "./hrUtils";
import "./HRShared.css";
import "./Interviews.css";

const FILTERS = ["today", "upcoming", "completed", "cancelled"];

const ROUNDS = [
  "HR Screening",
  "Technical Round",
  "Manager Round",
  "Practical Test",
  "Final Round",
];

const STATUSES = [
  "Scheduled",
  "Completed",
  "Rescheduled",
  "Cancelled",
  "No Show",
  "Selected",
  "Rejected",
  "On Hold",
];

const emptyForm = {
  candidate: "",
  interviewDate: "",
  interviewTime: "",
  durationMinutes: 60,
  round: "HR Screening",
  interviewType: "Online",
  mode: "Google Meet",
  interviewerName: "",
  interviewerEmail: "",
  ccUsers: [],
  ccEmails: "",
  location: "",
  status: "Scheduled",
  feedback: "",
};

const getUserName = (user) =>
  user?.name ||
  user?.fullName ||
  [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
  user?.email ||
  "User";

const Interviews = () => {
  const [items, setItems] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [erpUsers, setErpUsers] = useState([]);

  const [filter, setFilter] = useState("upcoming");
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [ccSearch, setCcSearch] = useState("");
  const [ccOpen, setCcOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal open ho tab background page scroll ko lock rakhta hai.
  useEffect(() => {
    if (!modalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [modalOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [interviewResponse, candidateResponse, userResponse] =
        await Promise.all([
          getInterviews({ view: filter }),
          getCandidates({ limit: 500 }),
          getInterviewUsers({ limit: 1000 }),
        ]);

      setItems(normalizeList(interviewResponse));
      setCandidates(normalizeList(candidateResponse));
      setErpUsers(normalizeList(userResponse));
    } catch (requestError) {
      setError(requestError?.message || "Unable to load interview data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filter]);

  const selectedUserIds = useMemo(
    () => new Set((form.ccUsers || []).map(String)),
    [form.ccUsers],
  );

  const filteredUsers = useMemo(() => {
    const query = ccSearch.trim().toLowerCase();

    return erpUsers.filter((user) => {
      const haystack = [
        getUserName(user),
        user.email,
        user.role,
        user.department,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return !query || haystack.includes(query);
    });
  }, [erpUsers, ccSearch]);

  const selectedUsers = useMemo(
    () => erpUsers.filter((user) => selectedUserIds.has(String(user._id))),
    [erpUsers, selectedUserIds],
  );

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const openCreate = () => {
    setEditingId("");
    setForm({ ...emptyForm });
    setCcSearch("");
    setCcOpen(false);
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);

    setForm({
      ...emptyForm,
      ...item,
      candidate: item.candidate?._id || item.candidate || "",
      interviewDate: String(item.interviewDate || "").slice(0, 10),
      durationMinutes: Number(item.durationMinutes || 60),
      ccUsers: Array.isArray(item.ccUsers)
        ? item.ccUsers.map((user) => user?._id || user).filter(Boolean)
        : [],
      ccEmails: Array.isArray(item.ccEmails)
        ? item.ccEmails.join(", ")
        : item.ccEmails || "",
    });

    setCcSearch("");
    setCcOpen(false);
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingId("");
    setForm({ ...emptyForm });
    setCcSearch("");
    setCcOpen(false);
  };

  const changeInterviewType = (interviewType) => {
    setForm((current) => ({
      ...current,
      interviewType,
      mode: interviewType === "Online" ? "Google Meet" : "Office",
      location: interviewType === "Online" ? "" : current.location,
    }));
  };

  const toggleCcUser = (userId) => {
    setForm((current) => {
      const exists = (current.ccUsers || [])
        .map(String)
        .includes(String(userId));

      return {
        ...current,
        ccUsers: exists
          ? current.ccUsers.filter((id) => String(id) !== String(userId))
          : [...current.ccUsers, userId],
      };
    });

    // ERP user select/remove karte hi dropdown close ho jayega.
    setCcOpen(false);
    setCcSearch("");
  };

  const removeCcUser = (userId) => {
    setForm((current) => ({
      ...current,
      ccUsers: (current.ccUsers || []).filter(
        (id) => String(id) !== String(userId),
      ),
    }));
  };

  const saveInterview = async (event) => {
    event.preventDefault();

    if (
      form.interviewType === "Walk-in" &&
      !String(form.location || "").trim()
    ) {
      setError("Walk-in interview location is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        ...form,
        durationMinutes: Number(form.durationMinutes || 60),
        mode: form.interviewType === "Online" ? "Google Meet" : "Office",
        interviewerName: String(form.interviewerName || "").trim(),
        interviewerEmail: String(form.interviewerEmail || "")
          .trim()
          .toLowerCase(),
        ccUsers: (form.ccUsers || []).map((user) => user?._id || user),
        ccEmails: String(form.ccEmails || "")
          .split(",")
          .map((email) => email.trim().toLowerCase())
          .filter(Boolean),
        location:
          form.interviewType === "Walk-in"
            ? String(form.location || "").trim()
            : "",
      };

      const response = editingId
        ? await updateInterview(editingId, payload)
        : await createInterview(payload);

      /*
       * Interview ko Selected karte hi Candidate record ka status bhi
       * Selected ho jayega. Offer page isi Candidate status ko use karega.
       */
      if (
        String(form.status || "")
          .trim()
          .toLowerCase() === "selected" &&
        form.candidate
      ) {
        await updateCandidate(form.candidate, {
          status: "Selected",
        });
      }

      setSuccess(
        response?.data?.message ||
          (editingId
            ? "Interview updated and attendees notified."
            : "Interview scheduled and invitations sent."),
      );

      closeModal();
      await loadData();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to save interview.",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeInterview = async (id) => {
    if (!window.confirm("Cancel this interview and notify all attendees?")) {
      return;
    }

    try {
      setDeletingId(id);
      setError("");
      setSuccess("");

      await deleteInterview(id);
      setSuccess("Interview cancelled and all attendees notified.");

      await loadData();
    } catch (requestError) {
      setError(requestError?.message || "Unable to cancel interview.");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="hr-page hr-interviews-page">
      <header className="hr-page-header">
        <div>
          <h1>Interviews</h1>
          <p>
            Schedule Google Meet or walk-in interviews and notify attendees.
          </p>
        </div>

        <button
          type="button"
          className="hr-btn hr-btn-primary"
          onClick={openCreate}
        >
          + Schedule Interview
        </button>
      </header>

      {error && <div className="hr-error">{error}</div>}
      {success && <div className="hr-interview-success">{success}</div>}

      <section className="hr-card hr-interview-filter-card">
        <div className="hr-interview-tabs">
          {FILTERS.map((value) => (
            <button
              type="button"
              key={value}
              className={`hr-interview-tab ${filter === value ? "active" : ""}`}
              onClick={() => setFilter(value)}
            >
              {value.charAt(0).toUpperCase() + value.slice(1)}
            </button>
          ))}
        </div>
      </section>

      {loading ? (
        <div className="hr-card hr-loading">Loading interviews...</div>
      ) : (
        <div className="hr-table-wrap hr-interview-table-wrap">
          <table className="hr-table hr-interview-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Position</th>
                <th>Date & Time</th>
                <th>Round</th>
                <th>Type</th>
                <th>Interviewer</th>
                <th>CC</th>
                <th>Meet / Location</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {items.length ? (
                items.map((item) => {
                  const ccCount =
                    (Array.isArray(item.ccUsers) ? item.ccUsers.length : 0) +
                    (Array.isArray(item.ccEmails) ? item.ccEmails.length : 0);

                  return (
                    <tr key={item._id}>
                      <td>
                        <strong>
                          {item.candidate?.candidateName ||
                            item.candidateName ||
                            "Candidate"}
                        </strong>
                        <small>{item.candidate?.email || ""}</small>
                      </td>

                      <td>
                        {item.jobOpening?.jobTitle ||
                          item.candidate?.jobOpening?.jobTitle ||
                          item.position ||
                          "-"}
                      </td>

                      <td>
                        <strong>{formatDate(item.interviewDate)}</strong>
                        <small>{item.interviewTime || "-"}</small>
                      </td>

                      <td>{item.round || "-"}</td>

                      <td>
                        <span className="hr-interview-type-badge">
                          {item.interviewType || "Online"}
                        </span>
                        <small>{item.mode || "-"}</small>
                      </td>

                      <td>
                        <strong>
                          {item.interviewerName ||
                            item.interviewer?.name ||
                            "-"}
                        </strong>
                        <small>{item.interviewerEmail || ""}</small>
                      </td>

                      <td>
                        <span className="hr-cc-badge">
                          {ccCount} attendee{ccCount === 1 ? "" : "s"}
                        </span>
                      </td>

                      <td>
                        {item.interviewType === "Online" ? (
                          item.meetingLink ? (
                            <a
                              className="hr-meet-link"
                              href={item.meetingLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Join Google Meet
                            </a>
                          ) : (
                            <span className="hr-muted">Meet link pending</span>
                          )
                        ) : (
                          <span className="hr-location">
                            {item.location || "-"}
                          </span>
                        )}
                      </td>

                      <td>
                        <span
                          className={`hr-status ${statusClass(item.status)}`}
                        >
                          {item.status || "Scheduled"}
                        </span>
                      </td>

                      <td>
                        <div className="hr-inline-actions">
                          <button
                            type="button"
                            className="hr-btn hr-btn-secondary"
                            onClick={() => openEdit(item)}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="hr-btn hr-btn-danger"
                            disabled={deletingId === item._id}
                            onClick={() => removeInterview(item._id)}
                          >
                            {deletingId === item._id
                              ? "Cancelling..."
                              : "Cancel"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="hr-empty">
                    No interviews found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="hr-modal-backdrop" onMouseDown={closeModal}>
            <form
              className="hr-modal hr-interview-modal"
              onMouseDown={(event) => event.stopPropagation()}
              onSubmit={saveInterview}
            >
              <div className="hr-modal-head">
                <div>
                  <h2>{editingId ? "Edit Interview" : "Schedule Interview"}</h2>
                  <p>
                    Google Meet link is generated automatically for online
                    interviews.
                  </p>
                </div>

                <button
                  type="button"
                  className="hr-modal-close"
                  onClick={closeModal}
                  disabled={saving}
                >
                  ×
                </button>
              </div>

              <div className="hr-modal-body">
                <section className="hr-interview-section">
                  <h3>Candidate & Schedule</h3>

                  <div className="hr-form-grid">
                    <div className="hr-field hr-field-full">
                      <label>Candidate *</label>
                      <select
                        required
                        className="hr-select"
                        value={form.candidate}
                        onChange={(event) =>
                          updateField("candidate", event.target.value)
                        }
                      >
                        <option value="">Select candidate</option>
                        {candidates.map((candidate) => (
                          <option value={candidate._id} key={candidate._id}>
                            {candidate.candidateName} —{" "}
                            {candidate.jobOpening?.jobTitle || "Position"}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="hr-field">
                      <label>Interview Date *</label>
                      <input
                        required
                        type="date"
                        className="hr-input"
                        value={form.interviewDate}
                        onChange={(event) =>
                          updateField("interviewDate", event.target.value)
                        }
                      />
                    </div>

                    <div className="hr-field">
                      <label>Interview Time *</label>
                      <input
                        required
                        type="time"
                        className="hr-input"
                        value={form.interviewTime}
                        onChange={(event) =>
                          updateField("interviewTime", event.target.value)
                        }
                      />
                    </div>

                    <div className="hr-field">
                      <label>Duration</label>
                      <select
                        className="hr-select"
                        value={form.durationMinutes}
                        onChange={(event) =>
                          updateField(
                            "durationMinutes",
                            Number(event.target.value),
                          )
                        }
                      >
                        <option value={30}>30 Minutes</option>
                        <option value={45}>45 Minutes</option>
                        <option value={60}>1 Hour</option>
                        <option value={90}>1 Hour 30 Minutes</option>
                        <option value={120}>2 Hours</option>
                      </select>
                    </div>

                    <div className="hr-field">
                      <label>Round</label>
                      <select
                        className="hr-select"
                        value={form.round}
                        onChange={(event) =>
                          updateField("round", event.target.value)
                        }
                      >
                        {ROUNDS.map((value) => (
                          <option value={value} key={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>

                <section className="hr-interview-section">
                  <h3>Interview Type</h3>

                  <div className="hr-type-card-grid">
                    <label
                      className={`hr-type-card ${
                        form.interviewType === "Online" ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="interviewType"
                        checked={form.interviewType === "Online"}
                        onChange={() => changeInterviewType("Online")}
                      />
                      <strong>Online Interview</strong>
                      <span>
                        Google Meet link will be created automatically.
                      </span>
                    </label>

                    <label
                      className={`hr-type-card ${
                        form.interviewType === "Walk-in" ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="interviewType"
                        checked={form.interviewType === "Walk-in"}
                        onChange={() => changeInterviewType("Walk-in")}
                      />
                      <strong>Walk-in Interview</strong>
                      <span>
                        Calendar invite will include the office location.
                      </span>
                    </label>
                  </div>

                  {form.interviewType === "Online" ? (
                    <div className="hr-info-box">
                      There is no need to enter the Google Meet link manually.
                      The backend will automatically generate the link.
                    </div>
                  ) : (
                    <div className="hr-field hr-location-field">
                      <label>Complete Interview Location *</label>
                      <textarea
                        required
                        className="hr-textarea"
                        placeholder="Office name, floor, building, street and city"
                        value={form.location}
                        onChange={(event) =>
                          updateField("location", event.target.value)
                        }
                      />
                    </div>
                  )}
                </section>

                <section className="hr-interview-section">
                  <h3>Interviewer & Attendees</h3>

                  <div className="hr-form-grid">
                    <div className="hr-field">
                      <label>Interviewer Name</label>
                      <input
                        className="hr-input"
                        value={form.interviewerName}
                        onChange={(event) =>
                          updateField("interviewerName", event.target.value)
                        }
                      />
                    </div>

                    <div className="hr-field">
                      <label>Interviewer Email</label>
                      <input
                        type="email"
                        className="hr-input"
                        placeholder="interviewer@company.com"
                        value={form.interviewerEmail}
                        onChange={(event) =>
                          updateField("interviewerEmail", event.target.value)
                        }
                      />
                    </div>

                    <div className="hr-field hr-field-full">
                      <label>Add ERP Users in CC</label>

                      <div className="hr-user-multiselect">
                        <button
                          type="button"
                          className="hr-user-select-trigger"
                          onClick={() => setCcOpen((current) => !current)}
                        >
                          <span>
                            {selectedUsers.length
                              ? `${selectedUsers.length} user(s) selected`
                              : "Select ERP users"}
                          </span>
                          <span>{ccOpen ? "▴" : "▾"}</span>
                        </button>

                        {!!selectedUsers.length && (
                          <div className="hr-selected-users">
                            {selectedUsers.map((user) => (
                              <span
                                className="hr-selected-user-chip"
                                key={user._id}
                              >
                                {getUserName(user)}
                                <button
                                  type="button"
                                  onClick={() => removeCcUser(user._id)}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        {ccOpen && (
                          <div className="hr-user-dropdown">
                            <input
                              type="search"
                              className="hr-input"
                              placeholder="Search user, email, role..."
                              value={ccSearch}
                              onChange={(event) =>
                                setCcSearch(event.target.value)
                              }
                            />

                            <div className="hr-user-options">
                              {filteredUsers.map((user) => (
                                <label
                                  className="hr-user-option"
                                  key={user._id}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedUserIds.has(
                                      String(user._id),
                                    )}
                                    onChange={() => toggleCcUser(user._id)}
                                  />

                                  <span className="hr-user-avatar">
                                    {getUserName(user).charAt(0).toUpperCase()}
                                  </span>

                                  <span>
                                    <strong>{getUserName(user)}</strong>
                                    <small>
                                      {user.email || "No email"}
                                      {user.role ? ` • ${user.role}` : ""}
                                    </small>
                                  </span>
                                </label>
                              ))}

                              {!filteredUsers.length && (
                                <div className="hr-empty-user">
                                  No ERP users found.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="hr-field hr-field-full">
                      <label>Additional External CC Emails</label>
                      <input
                        className="hr-input"
                        placeholder="manager@example.com, client@example.com"
                        value={form.ccEmails}
                        onChange={(event) =>
                          updateField("ccEmails", event.target.value)
                        }
                      />
                      <small>
                        Separate multiple email addresses with commas.
                      </small>
                    </div>
                  </div>
                </section>

                <section className="hr-interview-section">
                  <h3>Status & Feedback</h3>

                  <div className="hr-form-grid">
                    <div className="hr-field">
                      <label>Status</label>
                      <select
                        className="hr-select"
                        value={form.status}
                        onChange={(event) =>
                          updateField("status", event.target.value)
                        }
                      >
                        {STATUSES.map((value) => (
                          <option value={value} key={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="hr-field hr-field-full">
                      <label>Feedback</label>
                      <textarea
                        className="hr-textarea"
                        value={form.feedback}
                        onChange={(event) =>
                          updateField("feedback", event.target.value)
                        }
                      />
                    </div>
                  </div>
                </section>
              </div>

              <div className="hr-modal-foot">
                <button
                  type="button"
                  className="hr-btn hr-btn-secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="hr-btn hr-btn-primary"
                  disabled={saving}
                >
                  {saving
                    ? "Sending Invitations..."
                    : editingId
                      ? "Update & Notify"
                      : "Schedule & Send Invite"}
                </button>
              </div>
            </form>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Interviews;
